import { FC, useRef, useEffect, useState, SyntheticEvent } from 'react';
import { Interpolation, Theme } from '@emotion/react';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { Button } from '@frontend/design-system';
import { getStatusIconName } from '../../utils';
import { buttonStyle, containerStyle } from './status-updated.styles';

interface StatusUpdaterProps {
  onAction: (e: SyntheticEvent<HTMLButtonElement>) => void;
  status?: TaskCenterTypes.TaskStatus;
  css?: Interpolation<Theme>;
  trackingId: string;
  size?: 'medium' | 'large';
}

export const StatusUpdater: FC<StatusUpdaterProps> = ({ status, onAction, css, trackingId, size = 'medium' }) => {
  const { t } = useTranslation('task-center');
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const spanRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { name } = getStatusIconName(status);

  const isIncomplete = status !== TaskCenterTypes.TaskStatus.STATUS_COMPLETED;

  const shouldShowMarkAsDone = isIncomplete && isHovering;

  useEffect(() => {
    const spanRefObj = spanRef.current;

    const onEnter = () => setIsHovering(true);

    const onLeave = () => setIsHovering(false);

    spanRefObj?.addEventListener('mouseenter', onEnter);
    spanRefObj?.addEventListener('mouseleave', onLeave);

    spanRefObj?.addEventListener('focus', onEnter);
    spanRefObj?.addEventListener('blur', onLeave);

    spanRefObj?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        buttonRef.current?.click();
      }
    });
    return () => {
      spanRefObj?.removeEventListener('mouseenter', onEnter);
      spanRefObj?.removeEventListener('mouseleave', onLeave);

      spanRefObj?.removeEventListener('focus', onEnter);
      spanRefObj?.removeEventListener('blur', onLeave);
    };
  }, []);

  return (
    <span ref={spanRef} aria-label={t('Mark as Done')} tabIndex={0} css={[containerStyle, css]}>
      {shouldShowMarkAsDone ? (
        <Button
          ref={buttonRef}
          size={size}
          iconName='mark-done'
          onClick={onAction}
          variant='tertiary'
          css={buttonStyle}
          trackingId={trackingId}
          hoverLabel={t('Mark as Done')}
        />
      ) : (
        <Icon name={name} size={size === 'large' ? 20 : 16} />
      )}
    </span>
  );
};
